import React, { useEffect, useRef, useState } from 'react';
import { User, Crown, Shield, Clock, MoreVertical, Pencil, Trash2 } from 'lucide-react';

const UserCard = ({ user, onToggleStatus, onEditUser, onDeleteUser, loading }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return 'Mai';
    return new Date(dateString)?.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin':
        return <Crown className="w-4 h-4 text-yellow-600" />;
      case 'order_user':
        return <User className="w-4 h-4 text-blue-600" />;
      default:
        return <Shield className="w-4 h-4 text-gray-600" />;
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'admin':
        return 'Amministratore';
      case 'order_user':
        return 'Venditore';
      default:
        return 'Utente';
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 hover:shadow-md transition-shadow overflow-visible">
      {/* User Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center">
              <User className="h-6 w-6 text-gray-600" />
            </div>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-gray-900">{user?.full_name}</h3>
            <p className="text-sm text-gray-500">@{user?.username}</p>
          </div>
        </div>
        <div className="flex-shrink-0 relative" ref={menuRef}>
          <button
            onClick={() => setMenuOpen((prev) => !prev)}
            className="text-gray-400 hover:text-gray-600 p-1 rounded"
            title="Azioni utente"
          >
            <MoreVertical className="h-4 w-4" />
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-full mt-2 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
              <button
                onClick={() => {
                  setMenuOpen(false);
                  onEditUser?.(user);
                }}
                className="w-full flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-t-lg"
              >
                <Pencil className="w-4 h-4 mr-2" />
                Modifica
              </button>
              <button
                onClick={() => {
                  setMenuOpen(false);
                  onDeleteUser?.(user);
                }}
                className="w-full flex items-center px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-b-lg"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Elimina
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Role Badge */}
      <div className="flex items-center mb-3">
        {getRoleIcon(user?.role)}
        <span className="ml-2 text-sm font-medium text-gray-700">
          {getRoleLabel(user?.role)}
        </span>
      </div>

      {/* Status Badge */}
      <div className="mb-4">
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          user?.is_active 
            ? 'bg-green-100 text-green-800' :'bg-red-100 text-red-800'
        }`}>
          {user?.is_active ? 'Attivo' : 'Disattivato'}
        </span>
      </div>

      {/* Last Login */}
      <div className="flex items-start mb-4 text-sm text-gray-500">
        <Clock className="w-4 h-4 mr-2" />
        <span className="break-words">Ultimo accesso: {formatDate(user?.last_login_at)}</span>
      </div>

      {/* Actions */}
      <div className="flex items-center space-x-2">
        <button
          onClick={() => onToggleStatus?.(user?.id, user?.is_active)}
          disabled={loading}
          className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 ${
            user?.is_active
              ? 'text-red-700 bg-red-100 hover:bg-red-200 focus:ring-red-500' :'text-green-700 bg-green-100 hover:bg-green-200 focus:ring-green-500'
          } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {loading ? 'Caricamento...' : (user?.is_active ? 'Disattiva' : 'Attiva')}
        </button>
      </div>

      {/* Created Date */}
      <div className="mt-3 pt-3 border-t border-gray-100">
        <p className="text-xs text-gray-400">
          Creato: {formatDate(user?.created_at)}
        </p>
      </div>
    </div>
  );
};

export default UserCard;
